import React, {useContext, useMemo} from 'react'
import PropTypes from 'prop-types'
import {isServer} from '../utils/utils'

const PageCacheContext = React.createContext()
const PageCacheProvider = PageCacheContext.Provider

/**
 * Global cache for all pages - as we know that a single page can be rendered at a time, we use the same global cache
 * to store the cache for all pages. As it only keeps the cache from the current page key (pathname), it is safe to use
 * without risk of memory leaks and too stale data.
 *
 * The cache is stored in the format:
 * @type {{[string]: {[string]: {[string]: any}}}}
 *
 * The first key is the page key (pathname), the second key is the type of cache (e.g. 'product', 'category', etc.) and
 * the third key is the cache key.
 */
let globalPageCache = {}

/**
 * Class to handle the cache for a single page.
 * @private exported for testing
 */
export class PageCache {
    /**
     * @param {string} pageKey - the key for the page cache, usually pathname
     */
    constructor(pageKey) {
        this._pageKey = pageKey
        // this clears cache for other pages
        globalPageCache = {
            [pageKey]: globalPageCache?.[pageKey] || {}
        }
    }

    /**
     * Get cached value by type and key
     * @param {string} type - the type of cache
     * @param {string} key - the key for the cache
     * @return {*|undefined} - the cached value or undefined if not found
     */
    get(type, key) {
        return globalPageCache[this._pageKey]?.[type]?.[key]
    }

    /**
     * Set cached value by type and key
     * @param {string} type - the type of cache
     * @param {string} key - the key for the cache
     * @param {*} value - the value to cache. Must be serializable.
     */
    set(type, key, value) {
        // in case the cache for this page key was cleared, do not set the value
        // this could happen when navigating to different page, but some async operation is still running
        if (!globalPageCache[this._pageKey]) {
            return
        }
        globalPageCache[this._pageKey][type] = globalPageCache[this._pageKey][type] || {}
        globalPageCache[this._pageKey][type][key] = value
    }
}

/**
 * HOC to wrap a page component and provide it with a cache for use in getProps.
 *
 * getProps will be called with an additional argument `cache`
 * which is an instance of PageCache ({
 *   get(type: string, key: sting): any,
 *   set(type: string, key: string, value: any): void
 * }).
 */
const withPageCache = (Page) => {
    const WrappedComponent = ({cache, location, pageProps, ...pwaSystemProps}) => {
        // persist the cache value received from the server, unless the location is not set (tests)
        globalPageCache = location?.pathname != null ? cache || {} : {}

        const pageCache = useMemo(
            () => new PageCache(location?.pathname ?? ''),
            [location?.pathname]
        )
        // render the page with its props
        return (
            <PageCacheProvider value={pageCache}>
                <Page {...pwaSystemProps} location={location} {...pageProps} />
            </PageCacheProvider>
        )
    }
    if (process.env.NODE_ENV !== 'production') {
        WrappedComponent.displayName = `withCachedPageGetProps(${Page.displayName || Page.name})`
    }
    WrappedComponent.propTypes = {
        cache: PropTypes.object,
        location: PropTypes.object,
        pageProps: PropTypes.object
    }
    if (Page.getProps) {
        // default implementation calls getProps on pathname change
        if (Page.shouldGetProps) {
            WrappedComponent.shouldGetProps = (args) => {
                const {previousLocation, location} = args
                return (
                    // make sure to call getProps if the location has changed or the page requires it
                    previousLocation?.pathname !== location.pathname || Page.shouldGetProps(args)
                )
            }
        }

        WrappedComponent.getProps = async (context) => {
            // initialize the global page cache on server side, to prevent the cache from being shared between requests
            // and clear in tests without location
            if (isServer || context.location?.pathname == null) {
                globalPageCache = {}
            }
            // create a new cache instance
            const cache = new PageCache(context.location.pathname)
            // call the original getProps with the cache
            const pageProps = await Page.getProps({...context, cache})
            // return the cache and the pageProps
            return {cache: globalPageCache, pageProps}
        }
    }
    WrappedComponent.getTemplateName = Page.getTemplateName

    return WrappedComponent
}

export function usePageCache() {
    const ctx = useContext(PageCacheContext)
    if (!ctx) {
        throw new Error('usePageCache must be used within a page wrapped with withPageCache')
    }
    return ctx
}

export default withPageCache
